import UploadIcon from "@mui/icons-material/Upload"
import {
	Alert,
	Button,
	CircularProgress,
	Grid,
	Paper,
	Snackbar,
	TextField,
	Typography,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { AxiosError, AxiosResponse } from "axios"
import { ChangeEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { ReactComponent as SettingSvg } from "../../../assets/img/settings.svg"
import { AppDispatch } from "../../../store"
import {
	CampaignsState,
	updateCampaign,
} from "../../../store/actions/campaigns"

interface ICreateFormProps {
	bank?: string | undefined
	isEditMode?: boolean
	campaign?: CampaignsState | undefined
	tabIndex?: number
	onClose: () => void
}

const CreateForm = ({
	bank,
	isEditMode,
	campaign,
	tabIndex,
}: ICreateFormProps) => {
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()
	const [alertMessage, setAlertMessage] = useState<string | undefined>(
		undefined
	)
	const [isErrorSnackBarOpen, setIsErrorSnackBarOpen] = useState<boolean>(false)
	const [isSavingForm, setIsSavingForm] = useState<boolean>(false)
	const [isSaved, setIsSaved] = useState<boolean>(false)
	const [formData, setFormData] = useState<CampaignsState>({
		creationDate: undefined,
		modificationDate: undefined,
		createdBy: undefined,
		modifiedBy: undefined,
		version: 0,
		id: -1,
		idLogo: undefined,
		campaignName: undefined,
		campaignLogo: undefined,
		enabled: false,
		deleted: false,
		authorizationType: undefined,
	})
	const [updateFormData, setUpdateFormData] = useState<
		CampaignsState | undefined
	>(undefined)
	const [errors, setErrors] = useState(undefined)
	const [campaignImgSrc, setCampaignImgSrc] = useState(undefined)

	const VisuallyHiddenInput = styled("input")({
		clip: "rect(0 0 0 0)",
		clipPath: "inset(50%)",
		height: 1,
		overflow: "hidden",
		position: "absolute",
		bottom: 0,
		left: 0,
		whiteSpace: "nowrap",
		width: 1,
	})

	useEffect(() => {
		if (isEditMode) {
			setFormData({
				creationDate: campaign?.creationDate,
				modificationDate: campaign?.modificationDate,
				createdBy: campaign?.createdBy,
				modifiedBy: campaign?.modifiedBy,
				version: campaign?.version ?? -1,
				id: campaign?.id ?? -1,
				idLogo: campaign?.idLogo,
				campaignName: campaign?.campaignName,
				campaignLogo: campaign?.campaignLogo,
				enabled: campaign?.enabled ?? false,
				deleted: campaign?.deleted ?? false,
				authorizationType: campaign?.authorizationType,
			})
		}
	}, [campaign?.id, isEditMode])

	useEffect(() => {
		if (formData?.campaignLogo) {
			const base64Image = formData.campaignLogo
			if (formData.campaignLogo instanceof File) {
				return null
			}
			const newcampaignImgSrc = base64Image && atob(base64Image)
			setCampaignImgSrc(newcampaignImgSrc)
		}
	}, [formData?.campaignLogo])

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}))
		if (isEditMode) {
			setUpdateFormData((prevData) => ({
				...prevData,
				[name]: value,
			}))
		}
	}

	const handleColorChange = (value: string, name: string) => {
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}))
		if (isEditMode) {
			setUpdateFormData((prevData) => ({
				...prevData,
				[name]: value,
			}))
		}
	}

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, files } = event.target
		console.log(name)
		if (files?.[0]) {
			if (isEditMode) {
				setUpdateFormData((prevData) => ({
					...prevData,
					[name]: files[0],
				}))
			}

			const reader = new FileReader()
			reader.onloadend = () => {
				setCampaignImgSrc(reader.result)
				setFormData((prevData) => ({
					...prevData,
					[name]: btoa(reader.result),
				}))
			}
			reader.readAsDataURL(files[0])
		}
	}

	const handleSaveForm = () => {
		setIsSavingForm(true)
		if (updateFormData && campaign?.id && bank)
			dispatch(
				updateCampaign({
					campaignData: updateFormData,
					idBank: bank,
					idCampaign: campaign?.id,
				})
			)
				.then((result: AxiosResponse) => {
					setIsSavingForm(false)
					if (result?.meta?.requestStatus === "rejected") {
						if (result?.payload?.status === 403) {
							setAlertMessage(
								t("unauthorized.error.alertMessage", { ns: "common" })
							)
							setIsErrorSnackBarOpen(true)
						} else if (result?.payload?.status === 400) {
							const backendErrors = result?.payload.data.reduce((acc, err) => {
								acc[err.field] = err.defaultMessage
								return acc
							}, {})
							setErrors(backendErrors)
						}
					} else {
						setIsSaved(true)
					}
				})
				.catch((error: AxiosError) => {
					if (error.response && error.response.status === 400) {
						const backendErrors = error.response.data.reduce((acc, err) => {
							acc[err.field] = err.defaultMessage
							return acc
						}, {})
						setErrors(backendErrors)
					} else if (error.response && error.response.status === 404) {
						setAlertMessage(
							t("unauthorized.error.alertMessage", { ns: "common" })
						)
						setIsErrorSnackBarOpen(true)
					}
				})
	}
	return (
		<>
			{isSaved ? (
				<Paper square elevation={0} sx={{ p: 4 }}>
					<Grid container>
						<Grid item>
							<SettingSvg width={"200px"} height={"200px"} />
						</Grid>
						<Grid item sx={{ alignContent: "center" }}>
							<Typography sx={{ fontWeight: "bold" }} component="div">
								{t(isEditMode ? "form.modified" : "form.created", {
									ns: "campaign",
									campaignName: formData?.campaignName,
								})}
							</Typography>
						</Grid>
					</Grid>
				</Paper>
			) : (
				<Grid
					container
					flexDirection={"column"}
					justifyContent={"space-between"}
				>
					<Grid container gap={"16px"} flexDirection={"column"}>
						<Grid item sx={{ paddingBottom: "34px" }}>
							<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
								<TextField
									fullWidth
									InputLabelProps={{
										shrink: Boolean(
											updateFormData?.campaignName || formData?.campaignName
										),
									}}
									error={!!errors?.campaignName}
									helperText={t(errors?.campaignName, { ns: "campaign" })}
									id="campaignName"
									label={t("form.campaignName", { ns: "campaign" })}
									name="campaignName"
									value={updateFormData?.campaignName || formData?.campaignName}
									onChange={handleInputChange}
									required
								/>
							</Grid>
							{/*
                            <Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
                                <TextField
                                    id="timetoexpire"
                                    InputLabelProps={{
                                        shrink: Boolean(
                                            updateFormData?.timeToExpiry || formData?.timeToExpiry
                                        ),
                                    }}
                                    label={t("form.timeExpire", { ns: "bank" })}
                                    name="timeToExpiry"
                                    value={
                                        updateFormData?.timeToExpiry ??
                                        parseInt(formData?.timeToExpiry) ??
                                        ""
                                    }
                                    onChange={handleInputChange}
                                    type="number"
                                />
                            </Grid>
                            */}
							<Grid item flexGrow={1} sx={{ marginBottom: "16px" }}>
								<img
									alt={t("logo.bank", { ns: "common" })}
									style={{
										maxWidth: "50px",
										height: "fit-content",
										verticalAlign: "middle",
									}}
									src={campaignImgSrc}
								/>
								<Button
									component="label"
									role={undefined}
									variant="contained"
									tabIndex={-1}
									startIcon={<UploadIcon />}
								>
									Cambia logo
									<VisuallyHiddenInput
										name="campaignImage"
										type="file"
										onChange={(event) => handleFileChange(event)}
									/>
								</Button>
							</Grid>

							{/*
                            <Grid item flexGrow={1}>
                                <FormControl>
                                    <FormLabel id="radio-auto-accept">
                                        {t("form.autoAccept", { ns: "bank" })}
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="radio-auto-accept"
                                        name="row-radio-auto-accept-group"
                                    >
                                        <FormControlLabel
                                            checked={Boolean(formData?.autoAccept)}
                                            control={<Radio />}
                                            label="Attivo"
                                        />
                                        <FormControlLabel
                                            checked={Boolean(!formData?.autoAccept)}
                                            control={<Radio />}
                                            label="Disattivo"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item flexGrow={1}>
                                <FormControl>
                                    <FormLabel id="radio-check-iban">
                                        {t("form.checkIban", { ns: "bank" })}
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="radio-check-iban"
                                        name="row-radio-check-iban-group"
                                    >
                                        <FormControlLabel
                                            checked={formData?.checkIban}
                                            control={<Radio />}
                                            label="Attivo"
                                        />
                                        <FormControlLabel
                                            checked={!formData?.checkIban}
                                            control={<Radio />}
                                            label="Disattivo"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item flexGrow={1}>
                                <FormControl>
                                    <FormLabel id="radio-whatsapp">Whatsapp</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="radio-whatsapp"
                                        name="row-radio-whatsapp-group"
                                    >
                                        <FormControlLabel
                                            checked={formData?.whatsapp}
                                            control={<Radio />}
                                            label="Attivo"
                                        />
                                        <FormControlLabel
                                            checked={!formData?.whatsapp}
                                            control={<Radio />}
                                            label="Disattivo"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            */}
						</Grid>
					</Grid>
					<Grid
						item
						sx={{
							position: "fixed",
							width: "100%",
							bottom: "16px",
							backgroundColor: "#FFF",
							zIndex: 9,
						}}
					>
						{isSavingForm ? (
							<CircularProgress size="50px" />
						) : (
							<Button
								variant="contained"
								onClick={handleSaveForm}
								sx={{ margin: "16px 0" }}
							>
								{t("form.buttons.save", { ns: "bank" })}
							</Button>
						)}
					</Grid>
				</Grid>
			)}
			{!!alertMessage && (
				<Snackbar
					sx={{ width: "calc(200% - 50px)" }}
					anchorOrigin={{ vertical: "top", horizontal: "left" }}
					open={isErrorSnackBarOpen}
					autoHideDuration={30000}
					onClose={() => setIsErrorSnackBarOpen(false)}
				>
					<Alert
						onClose={() => setIsErrorSnackBarOpen(false)}
						severity="error"
						variant="filled"
						sx={{ width: "200%" }}
					>
						{alertMessage}
					</Alert>
				</Snackbar>
			)}
		</>
	)
}

export default CreateForm
